import {
  forwardRef, useId, useImperativeHandle, useRef, useState,
  type InputHTMLAttributes, type KeyboardEvent, type MouseEvent,
} from 'react'
import { X } from 'lucide-react'
import { cx } from './cx'
import s from './TagInput.module.css'

export interface TagInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'defaultValue' | 'onChange' | 'type' | 'size'> {
  /** The tags the field holds, in order. Controlled: the host owns them. */
  value: readonly string[]
  onChange: (next: string[]) => void
  /**
   * Turns what was typed into a tag, or refuses it with `null`. Defaults to
   * trimming, and refusing an empty string. A host with a vocabulary (a slug, a
   * case rule) says so here rather than cleaning up after `onChange`.
   */
  normalize?: (raw: string) => string | null
  /** The words of each chip's remove button. Defaults to "Remove <tag>". */
  removeLabel?: (tag: string) => string
  /**
   * The tags a person is likely to want — the field's vocabulary. While they
   * type, the ones containing what was typed (and not already held) are
   * offered in a listbox under the slot; a tag outside the list can still be
   * typed, unless `normalize` refuses it.
   */
  suggestions?: readonly string[]
}

/** How many suggestions the list offers at once: a list, not a catalogue. */
const MAX_SUGGESTIONS = 8

/** Suggestions containing the draft, those that START with it first, held tags left out. */
function matching(suggestions: readonly string[], draft: string, held: readonly string[]): string[] {
  const q = draft.trim().toLocaleLowerCase()
  if (!q) return []
  const hit = suggestions.filter((tag, i) => suggestions.indexOf(tag) === i && !held.includes(tag)
    && tag.toLocaleLowerCase().includes(q))
  const starts = hit.filter((tag) => tag.toLocaleLowerCase().startsWith(q))
  return [...starts, ...hit.filter((tag) => !starts.includes(tag))].slice(0, MAX_SUGGESTIONS)
}

const defaultNormalize = (raw: string): string | null => {
  const tag = raw.trim()
  return tag.length > 0 ? tag : null
}

/**
 * TAGS INSIDE ONE SLOT (#2406, "Input Controls and Validation": "Relationship
 * tags — Customer × · EU × · Managed × · Add a tag…").
 *
 * The chips are SEATED blocks inside the slot's recess, each with its own
 * remove control, and the slot ends in an input that takes the next one. The
 * whole thing is one value, so it is one slot with the value slot's states.
 *
 * ── keyboard, which is the part a drawn chip list usually loses ────────────
 *   · Enter or a comma commits what was typed. Enter with nothing typed is
 *     left alone, so it still submits the form the field sits in.
 *   · Backspace in an EMPTY input removes the last tag — the move every tag
 *     field has taught people — and says so.
 *   · Each chip's × is a real button, named "Remove <tag>", in the tab order;
 *     removing one returns focus to the input rather than dropping it on the
 *     page.
 *   · A duplicate is refused IN WORDS, not silently swallowed.
 * Every add, removal and refusal is spoken through one polite live region, so
 * a reader who cannot see the chips appear still knows what the field holds.
 *
 * ── suggestions: the field is a COMBOBOX when it has a vocabulary ─────────
 * (#2406 b3-controls, an audit gap: a tag field with no suggestions makes
 * every person re-type, and mis-type, a vocabulary the host already knows.)
 * Given `suggestions`, typing offers the matching ones in a listbox under the
 * slot. The input is the combobox that owns it — `aria-expanded`,
 * `aria-controls`, and `aria-activedescendant` for the option the arrows moved
 * to, so focus never leaves the input:
 *   · ArrowDown / ArrowUp move through the options (wrapping); nothing is
 *     active until an arrow is pressed, so Enter still adds what was TYPED;
 *   · Enter on an active option adds that option; a press on one does too;
 *   · Escape closes the list and keeps the draft.
 * Without `suggestions` the input is a plain text field, as before.
 *
 * ── why the chips are drawn here and not with `FilterChip` ────────────────
 * `FilterChip` draws a COMMITTED FILTER: the brand-tinted on-state of a
 * `Chip`. A tag is a value seated in its slot — the plate, the seat shadow,
 * the inner radius — so it is a different material, not a restyle. (Its
 * remove control is now named after what it holds, as a tag's is here.)
 */
export const TagInput = forwardRef<HTMLInputElement, TagInputProps>(function TagInput(
  {
    value, onChange, normalize = defaultNormalize, removeLabel = (tag) => `Remove ${tag}`, suggestions,
    className, style, disabled, readOnly, placeholder = 'Add a tag…', onKeyDown, ...rest
  },
  ref,
) {
  const input = useRef<HTMLInputElement>(null)
  useImperativeHandle(ref, () => input.current as HTMLInputElement)
  const [draft, setDraft] = useState('')
  const [said, setSaid] = useState('')
  const [active, setActive] = useState(-1)
  const [dismissed, setDismissed] = useState(false)
  const base = useId()
  const summaryId = `${base}-held`
  const listId = `${base}-suggestions`
  const editable = !disabled && !readOnly
  const offered = editable && suggestions ? matching(suggestions, draft, value) : []
  const listed = offered.length > 0 && !dismissed
  const activeIndex = listed && active < offered.length ? active : -1

  const commit = (raw: string = draft) => {
    const tag = normalize(raw)
    if (tag == null) return false
    if (value.includes(tag)) {
      setSaid(`${tag} is already added.`)
      return true
    }
    onChange([...value, tag])
    setDraft('')
    setActive(-1)
    setSaid(`Added ${tag}.`)
    return true
  }

  const remove = (tag: string) => {
    onChange(value.filter((held) => held !== tag))
    setSaid(`Removed ${tag}.`)
    input.current?.focus()
  }

  const keyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event)
    if (event.defaultPrevented || !editable) return
    if (listed && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault()
      const step = event.key === 'ArrowDown' ? 1 : -1
      setActive(activeIndex < 0 ? (step > 0 ? 0 : offered.length - 1) : (activeIndex + step + offered.length) % offered.length)
      return
    }
    if (listed && event.key === 'Escape') {
      event.preventDefault()
      setDismissed(true)
      setActive(-1)
      return
    }
    if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault()
      commit(offered[activeIndex]!)
      return
    }
    if ((event.key === 'Enter' || event.key === ',') && draft.trim().length > 0) {
      event.preventDefault()
      commit()
    } else if (event.key === ',') {
      event.preventDefault()
    } else if (event.key === 'Backspace' && draft.length === 0 && value.length > 0) {
      event.preventDefault()
      remove(value[value.length - 1]!)
    }
  }

  /* A press on the slot's own ground focuses the input, as the padding of a
     plain field would. A press on a chip's button is that button's. */
  const focusInput = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget || disabled) return
    event.preventDefault()
    input.current?.focus()
  }

  /* What the field HOLDS is the input's description — as one plain sentence,
     not the chip list itself, whose text would also read every "Remove …"
     button name into it. */
  const describedBy = [rest['aria-describedby'], value.length > 0 ? summaryId : null]
    .filter(Boolean).join(' ') || undefined

  return (
    // The wrapper forwards a pointer press to the input it wraps; the input
    // and each chip's button are the controls, so no role belongs here.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div className={cx(s.slot, className)} style={style} data-tag-input="" onMouseDown={focusInput}>
      {value.length > 0 && (
        <ul className={s.tags} aria-label="Tags">
          {value.map((tag) => (
            <li key={tag} className={s.tag}>
              <span className={s.word}>{tag}</span>
              {editable && (
                <button type="button" className={s.remove} aria-label={removeLabel(tag)}
                  onClick={() => remove(tag)}>
                  <X aria-hidden />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <input
        {...rest}
        ref={input}
        type="text"
        className={s.input}
        value={draft}
        disabled={disabled}
        readOnly={readOnly}
        placeholder={editable ? placeholder : undefined}
        aria-describedby={describedBy}
        {...(suggestions ? {
          role: 'combobox',
          'aria-autocomplete': 'list' as const,
          'aria-expanded': listed,
          'aria-controls': listed ? listId : undefined,
          'aria-activedescendant': activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined,
        } : {})}
        onChange={(event) => { setDraft(event.target.value); setActive(-1); setDismissed(false) }}
        onKeyDown={keyDown}
        onBlur={(event) => { rest.onBlur?.(event); if (editable) commit() }}
      />
      {listed && (
        <ul className={s.suggestions} role="listbox" id={listId} aria-label="Suggested tags">
          {offered.map((tag, i) => (
            // The input keeps focus and the keyboard (aria-activedescendant);
            // a pointer press is the only thing an option handles itself.
            <li key={tag} id={`${listId}-${i}`} role="option" aria-selected={i === activeIndex}
              className={s.suggestion}
              onMouseDown={(event) => { event.preventDefault(); commit(tag); input.current?.focus() }}>
              {tag}
            </li>
          ))}
        </ul>
      )}
      {value.length > 0 && <span className={s.hidden} id={summaryId}>{`Holds ${value.join(', ')}.`}</span>}
      <span className={s.hidden} role="status">{said}</span>
    </div>
  )
})
